import type {
  ISRCompanyUpdate,
  ISRLeadSubstatus,
  ISRLeadUpdateInput,
  ISRLeadUpdateParameters,
  ISRPersonUpdate,
  ISRSalesStatus,
} from '../types';
import { isISRNotesDocument } from './isrNotes';

const SUBSTATUS_BY_STATUS: Record<ISRSalesStatus, readonly ISRLeadSubstatus[]> = {
  // ENRICHED is the entry stage of the sales stepper — exactly ONE selectable
  // substatus: "Ready to Outreach" (the default).
  enriched: ['ready_to_outreach'],
  outreaching: ['not_started', 'icebreaker', 'follow_up', 'last_message'],
  // OUTREACHED is now a top-level STATUS with exactly one substatus.
  outreached: ['awaiting'],
  engaged: [
    'reply_received',
    'in_conversation',
    'meeting_requested',
    'meeting_scheduled',
    'discovery_completed',
    'qualified',
    'objection',
  ],
  proposal: [
    'proposal_prepared',
    'review_scheduled',
    'objections_resolved',
    'decision_process_identified',
    'next_action_scheduled',
  ],
  converted: ['closed_won', 'onboarding', 'active', 'upsell', 'support', 'at_risk'],
  discarded: [
    'not_interested',
    'wrong_person',
    'out_of_icp',
    'bad_timing',
    'no_response',
    'bounced',
  ],
};

export const ISR_SALES_SUBSTATUS_BY_STATUS = SUBSTATUS_BY_STATUS;

/**
 * Status and substatus are persisted through the existing `update_campaign`
 * workflow. Detail/notes saves still use the complete ISR snapshot contract,
 * but must not repeat that status mutation through `update_isr_lead`.
 */
export const withoutISRStatusMutation = (
  snapshot: ISRLeadUpdateInput['snapshot'],
): ISRLeadUpdateInput['snapshot'] => ({
  ...snapshot,
  lead_status: null,
  lead_substatus: null,
});

const PERSON_KEYS = [
  'id',
  'full_name',
  'role_position',
  'email_id',
  'phone_id',
  'mobile_id',
  'whatsapp_id',
  'linkedin_id',
  'twitter_x_id',
  'instagram_id',
  'facebook_id',
  'country',
  'state',
  'city',
  'full_address',
] as const;
const COMPANY_KEYS = [
  'id',
  'email_id',
  'phone_id',
  'mobile_id',
  'whatsapp_id',
  'website_id',
  'nr_employees',
  'linkedin_id',
  'twitter_x_id',
  'instagram_id',
  'facebook_id',
  'revenue_range',
  'cnae_code',
  'country',
  'state',
  'city',
  'full_address',
] as const;

const hasOnlyKeys = (value: object, allowedKeys: readonly string[]) =>
  Object.keys(value).every((key) => allowedKeys.includes(key));

const isIdentifier = (value: unknown): value is string | number =>
  (typeof value === 'string' && value.trim().length > 0) ||
  (typeof value === 'number' && Number.isFinite(value));

const isNullableText = (value: unknown) => value === null || typeof value === 'string';

const assertPerson = (person: ISRPersonUpdate): void => {
  if (
    !person ||
    !hasOnlyKeys(person, PERSON_KEYS) ||
    !isIdentifier(person.id) ||
    !isNullableText(person.full_name) ||
    !isNullableText(person.role_position) ||
    !isNullableText(person.email_id) ||
    !isNullableText(person.phone_id) ||
    !isNullableText(person.mobile_id) ||
    !isNullableText(person.whatsapp_id) ||
    !isNullableText(person.linkedin_id) ||
    !isNullableText(person.twitter_x_id) ||
    !isNullableText(person.instagram_id) ||
    !isNullableText(person.facebook_id) ||
    !isNullableText(person.country) ||
    !isNullableText(person.state) ||
    !isNullableText(person.city) ||
    !isNullableText(person.full_address)
  ) {
    throw new TypeError('Invalid complete SDR person snapshot');
  }
};

const assertCompany = (company: ISRCompanyUpdate): void => {
  if (
    !company ||
    !hasOnlyKeys(company, COMPANY_KEYS) ||
    !isIdentifier(company.id) ||
    !isNullableText(company.email_id) ||
    !isNullableText(company.phone_id) ||
    !isNullableText(company.mobile_id) ||
    !isNullableText(company.whatsapp_id) ||
    !isNullableText(company.website_id) ||
    !isNullableText(company.linkedin_id) ||
    !isNullableText(company.twitter_x_id) ||
    !isNullableText(company.instagram_id) ||
    !isNullableText(company.facebook_id) ||
    !isNullableText(company.revenue_range) ||
    !isNullableText(company.cnae_code) ||
    !isNullableText(company.country) ||
    !isNullableText(company.state) ||
    !isNullableText(company.city) ||
    !isNullableText(company.full_address) ||
    !(
      company.nr_employees === null ||
      typeof company.nr_employees === 'string' ||
      (typeof company.nr_employees === 'number' &&
        Number.isFinite(company.nr_employees) &&
        company.nr_employees >= 0)
    )
  ) {
    throw new TypeError('Invalid complete SDR company snapshot');
  }
};

const assertSalesState = (
  status: ISRSalesStatus | null,
  substatus: ISRLeadSubstatus | null,
): void => {
  if (status === null) {
    if (substatus !== null) throw new TypeError('SDR substatus requires a Sales Intelligence status');
    return;
  }
  if (!(status in SUBSTATUS_BY_STATUS)) throw new TypeError('Invalid SDR Sales Intelligence status');
  if (substatus !== null && !SUBSTATUS_BY_STATUS[status].includes(substatus)) {
    throw new TypeError('SDR substatus does not belong to the selected sales status');
  }
};

/** Build the exact full-snapshot parameters consumed by the update_isr_lead workflow. */
export const buildISRUpdatePayload = (input: ISRLeadUpdateInput): ISRLeadUpdateParameters => {
  if (!input || !isIdentifier(input.identity?.campaign_id) || !isIdentifier(input.identity?.lvl2_id)) {
    throw new TypeError('campaign_id and lvl2_id are required for an SDR update');
  }

  const snapshot = input.snapshot;
  if (!snapshot) throw new TypeError('A complete SDR update snapshot is required');
  assertPerson(snapshot.person);
  assertCompany(snapshot.company);
  assertSalesState(snapshot.lead_status, snapshot.lead_substatus);
  if (!isISRNotesDocument(snapshot.notes)) {
    throw new TypeError('Every SDR update must include the complete notes JSON document');
  }

  const metadata = input.metadata;
  if (metadata?.changed_fields && !metadata.changed_fields.every((field) => typeof field === 'string')) {
    throw new TypeError('changed_fields must contain field names only');
  }
  if (metadata?.previous_values) {
    for (const [key, val] of Object.entries(metadata.previous_values)) {
      if (val !== null && typeof val !== 'string') {
        throw new TypeError(`previous_values["${key}"] must be a string or null`);
      }
    }
  }

  return {
    campaign_id: String(input.identity.campaign_id),
    lvl2_id: input.identity.lvl2_id,
    person: {
      id: snapshot.person.id,
      full_name: snapshot.person.full_name,
      role_position: snapshot.person.role_position,
      email_id: snapshot.person.email_id,
      phone_id: snapshot.person.phone_id,
      mobile_id: snapshot.person.mobile_id,
      whatsapp_id: snapshot.person.whatsapp_id,
      linkedin_id: snapshot.person.linkedin_id,
      twitter_x_id: snapshot.person.twitter_x_id,
      instagram_id: snapshot.person.instagram_id,
      facebook_id: snapshot.person.facebook_id,
      country: snapshot.person.country,
      state: snapshot.person.state,
      city: snapshot.person.city,
      full_address: snapshot.person.full_address,
    },
    company: {
      id: snapshot.company.id,
      email_id: snapshot.company.email_id,
      phone_id: snapshot.company.phone_id,
      mobile_id: snapshot.company.mobile_id,
      whatsapp_id: snapshot.company.whatsapp_id,
      website_id: snapshot.company.website_id,
      nr_employees: snapshot.company.nr_employees,
      linkedin_id: snapshot.company.linkedin_id,
      twitter_x_id: snapshot.company.twitter_x_id,
      instagram_id: snapshot.company.instagram_id,
      facebook_id: snapshot.company.facebook_id,
      revenue_range: snapshot.company.revenue_range,
      cnae_code: snapshot.company.cnae_code,
      country: snapshot.company.country,
      state: snapshot.company.state,
      city: snapshot.company.city,
      full_address: snapshot.company.full_address,
    },
    lead_status: snapshot.lead_status,
    lead_substatus: snapshot.lead_substatus,
    notes: {
      schema_version: 1,
      notes: snapshot.notes.notes.map((note) => ({
        id: note.id,
        body: note.body,
        created_at: note.created_at,
        ...(note.created_by !== undefined ? { created_by: note.created_by } : {}),
        ...(note.created_by_name !== undefined ? { created_by_name: note.created_by_name } : {}),
        ...(note.title !== undefined ? { title: note.title } : {}),
        ...(note.icon !== undefined ? { icon: note.icon } : {}),
        ...(note.activity_type !== undefined ? { activity_type: note.activity_type } : {}),
      })),
    },
    ...(metadata?.expected_version !== undefined
      ? { expected_version: metadata.expected_version }
      : {}),
    ...(metadata?.idempotency_key ? { idempotency_key: metadata.idempotency_key } : {}),
    ...(metadata?.changed_fields ? { changed_fields: [...metadata.changed_fields] } : {}),
    ...(metadata?.previous_values ? { previous_values: { ...metadata.previous_values } } : {}),
    ...(metadata?.updated ? { updated: metadata.updated } : {}),
    source: 'isr-workspace',
  };
};
